import { keepPreviousData, Updater, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AppID, Filter } from '@playq/octopus-common';
import {
  GameEventBrief,
  GameEventID,
  GameEventPromotions,
  GameEventsSort,
  QueryGameEventsResponse,
} from '@playq/octopus2-liveops';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';
import { PermModule } from '@playq/octopus2-auth';

import { IEntityPromotion } from '/api/hooks/promotions';
import {
  IUseQueryOptionsExtended,
  useEitherQuery,
  useMutateQueryDataAfterEntityDeleting,
  useQueryPrefetch,
} from '/api/service-hooks';
import { services2 } from '/api/services2';
import { iteratorMaxLimit } from '/constants';
import { appToolkit } from '/store';
import { useModulePermissions } from '/hooks';

import { gameEventsQueryKeys } from './constants';
import { createGEResponseAfterGEDeleting, mutatePromotionsAndFeaturesAfterGEDeleting } from './helpers';

const defaultQuery = {
  iterator: new OffsetLimit({ limit: iteratorMaxLimit, offset: 0 }),
  sortBy: [],
  filterBy: {},
};

const emptyArray: [] = [];

export const useGameEventsQuery = (
  iterator: OffsetLimit = defaultQuery.iterator,
  sortBy: GameEventsSort[] = defaultQuery.sortBy,
  filterBy: {
    [key: string]: Filter;
  } = defaultQuery.filterBy,
  options?: IUseQueryOptionsExtended<QueryGameEventsResponse | undefined, GenericFailure | Error>
) => {
  const appID = useSelector(appToolkit.selectors.appID);

  const { canRead } = useModulePermissions(PermModule.LiveOpsGameEvents);

  const queryClient = useQueryClient();

  const keys = useMemo(
    () => gameEventsQueryKeys.concat(appID, iterator, sortBy, filterBy),
    [appID, iterator, sortBy, filterBy]
  );

  const isValid = canRead && appID !== undefined;
  const enabled = options?.enabled !== false && isValid;
  const enablePrefetch = options?.enablePrefetch !== false && isValid;

  const { mutate, refetch, ...res } = useEitherQuery(
    keys,
    () => services2.appGameEventsService.queryGameEvents(appID as AppID, iterator, sortBy, filterBy),
    {
      placeholderData: keepPreviousData,
      ...options,
      enabled,
    }
  );

  const total = res.data?.total ?? 0;

  const { nextKeys } = useQueryPrefetch({
    keys,
    enabled: enablePrefetch,
    total,
    args: [appID as AppID, iterator, sortBy, filterBy],
    serviceMethod: services2.appGameEventsService.queryGameEvents.bind(services2.appGameEventsService),
  });

  const mutateUpdatePromotions = useCallback(
    (promotions: Updater<IEntityPromotion<GameEventID>[], IEntityPromotion<GameEventID>[]>) => {
      mutate((prevResponse) => {
        if (!prevResponse) {
          return;
        }
        const response = new QueryGameEventsResponse(prevResponse.serialize());
        response.gameevents = prevResponse.gameevents;
        response.iterator = prevResponse.iterator;
        response.features = prevResponse.features;

        const nextPromtions = typeof promotions === 'function' ? promotions(response.promotions) : promotions;
        response.promotions = nextPromtions.map(
          (p) => new GameEventPromotions({ id: p.id.serialize(), promotions: p.promotions.serialize() })
        );
        return response;
      });
    },
    [mutate]
  );

  const mutateDeletedEvent = useMutateQueryDataAfterEntityDeleting({
    queryClient,
    nextPageQueryKey: nextKeys,
    total,
    iterator,
    mutate,
    refetch,
    getID: (gE?: GameEventBrief) => gE?.id,
    getEntities: (r: QueryGameEventsResponse) => r.gameevents,
    setEntities: (r: QueryGameEventsResponse, newEntities: GameEventBrief[]) => {
      r.gameevents = newEntities;
    },
    createNewResponse: createGEResponseAfterGEDeleting,
    mutateCustomFields: mutatePromotionsAndFeaturesAfterGEDeleting,
  });

  return {
    ...res,
    events: res.data?.gameevents || emptyArray,
    promotions: (res.data?.promotions || emptyArray) as IEntityPromotion<GameEventID>[],
    total,
    features: res.data?.features || emptyArray,
    tags: keys,
    mutateUpdatePromotions,
    mutateDeletedEvent,
    refetch,
  };
};
